import { SelectCategorieDepense } from '../shared/SelectCategorieDepense';
import { useEffect, useMemo, useState } from 'react';
import { Upload, Sparkles, Trash2, Check, X, Camera, QrCode } from '../../lib/icons';
import { toast } from 'sonner';
import { qrDataUrl } from '../../lib/portail';
import { compresserFichier } from '../../lib/image';
import { deposerDocument } from '../../lib/docs';
import {
  analyserFacture,
  resoudreFournisseurGeneral,
  creerDepense,
  fetchBrouillons,
  ajouterBrouillon,
  supprimerBrouillon,
  soldeAvance,
  TAUX_TVA,
  TAUX_RS,
  TIMBRE_FISCAL,
} from '../../lib/dataProvider';
import { fmt } from '../shared/badges';
import { useAuth } from '../../lib/auth';

const inputCls = 'h-10 w-full border border-slate-300 bg-white px-2 font-data text-sm outline-none focus:border-slate-900';
const label = 'text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500';
// OCR en masse : glisser-déposer de plusieurs factures, brouillons à valider un par un.
export const LotFactures = ({ avances, onDone }) => {
  const { user } = useAuth();
  const [brouillons, setBrouillons] = useState(fetchBrouillons());
  const [progression, setProgression] = useState(null);
  const [ouvert, setOuvert] = useState(null);
  const [survol, setSurvol] = useState(false);
  const [qr, setQr] = useState(null);

  const rafraichir = () => setBrouillons(fetchBrouillons());

  // Un scan envoyé depuis le téléphone doit apparaître ici sans rechargement de page.
  useEffect(() => {
    const maj = () => setBrouillons(fetchBrouillons());
    window.addEventListener('store-maj', maj);
    return () => window.removeEventListener('store-maj', maj);
  }, []);

  const ouvrirQr = async () => {
    const lien = `${window.location.origin}/scan`;
    setQr({ lien, image: await qrDataUrl(lien) });
  };

  const traiter = async (files) => {
    const liste = Array.from(files ?? []).filter((f) => /\.(pdf|jpg|jpeg|png|webp|heic|heif)$/i.test(f.name) || f.type.startsWith('image/'));
    if (!liste.length) return toast.error('Formats acceptés : PDF, JPG, PNG.');
    for (const [i, brutFichier] of liste.entries()) {
      setProgression({ i: i + 1, total: liste.length, nom: brutFichier.name });
      let file = brutFichier;
      let apercuCompresse = null;
      if (brutFichier.type.startsWith('image/')) {
        try {
          const r = await compresserFichier(brutFichier);
          file = r.fichier;
          apercuCompresse = r.dataUrl;
        } catch (err) {
          toast.error(err.message);
          continue;
        }
      }
      const { error, facture } = await analyserFacture(file);
      if (error) {
        toast.error(`${file.name} : ${error}`);
        continue;
      }
      // Archivage au coffre-fort : un fichier déjà traité ne recrée pas de brouillon.
      const depot = await deposerDocument(file, {
        source: 'facture_frais',
        categorie: 'Facture fournisseur',
        deposePar: user?.name,
        indexer: false,
      });
      if (depot.doublon) {
        toast.warning(`${file.name} déjà traité (${depot.document.nom}) — aucun brouillon créé`, { duration: 9000 });
        continue;
      }
      ajouterBrouillon({
        id: `BR-${Date.now()}-${i}`,
        fichier: file.name,
        apercu: depot.url ?? apercuCompresse,
        documentId: depot.document?.id ?? null,
        mime: file.type,
        extrait: facture,
        source: 'poste',
        le: new Date().toISOString().slice(0, 16).replace('T', ' '),
      });
      rafraichir();
    }
    setProgression(null);
    toast.success(`${liste.length} document(s) analysé(s) — vérifiez les brouillons`);
  };

  return (
    <>
      <section data-testid="lot-factures" className="border border-slate-200 bg-white">
        <p className="border-b border-slate-200 px-4 py-3 font-heading text-base font-bold text-slate-900">
          Traitement par lot — zéro saisie <span className="font-data text-xs text-slate-500">(Gemini 3 Pro)</span>
        </p>
        <div className="p-4">
          <label
            data-testid="dropzone"
            onDragOver={(e) => { e.preventDefault(); setSurvol(true); }}
            onDragLeave={() => setSurvol(false)}
            onDrop={(e) => { e.preventDefault(); setSurvol(false); traiter(e.dataTransfer.files); }}
            className={`flex cursor-pointer flex-col items-center justify-center gap-2 border-2 border-dashed px-4 py-8 text-center transition-colors duration-150 ${
              progression ? 'border-violet-500 bg-violet-50' : survol ? 'border-slate-900 bg-slate-100' : 'border-slate-300 bg-slate-50 hover:border-slate-900'
            }`}
          >
            {progression ? <Sparkles size={22} className="animate-pulse text-violet-700" /> : <Upload size={22} className="text-slate-500" />}
            <span className="font-heading text-sm font-bold text-slate-900">
              {progression ? `Lecture IA ${progression.i}/${progression.total} — ${progression.nom}` : 'Glissez-déposez vos factures ici (PDF ou images, plusieurs à la fois)'}
            </span>
            <span className="text-xs text-slate-500">
              L'IA extrait fournisseur, date, n° de facture, ventilation multi-TVA, FODEC et timbre fiscal.
            </span>
            <input data-testid="lot-input" type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" disabled={Boolean(progression)} onChange={(e) => traiter(e.target.files)} />
          </label>

          <div className="mt-3 flex flex-wrap gap-2">
            <label data-testid="lot-camera" className="flex h-11 flex-1 min-w-[200px] cursor-pointer items-center justify-center gap-2 bg-slate-900 px-3 text-sm font-bold text-white hover:bg-slate-700">
              <Camera size={16} /> Scanner avec la caméra
              <input data-testid="lot-camera-input" type="file" accept="image/*" capture="environment" multiple className="hidden" disabled={Boolean(progression)} onChange={(e) => traiter(e.target.files)} />
            </label>
            <button type="button" data-testid="lot-qr" onClick={ouvrirQr} className="flex h-11 flex-1 min-w-[200px] items-center justify-center gap-2 border border-slate-900 px-3 text-sm font-bold text-slate-900 hover:bg-slate-900 hover:text-white">
              <QrCode size={16} /> Scanner depuis un téléphone
            </button>
          </div>
          <p className="mt-1.5 text-xs text-slate-500">
            Les factures photographiées au téléphone arrivent automatiquement dans la file de brouillons ci-dessous (base partagée).
          </p>

          {brouillons.length > 0 && (
            <div data-testid="liste-brouillons" className="mt-4 divide-y divide-slate-100 border border-slate-200">
              <p className="bg-slate-50 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                Factures en attente de validation ({brouillons.length})
              </p>
              {brouillons.map((b) => (
                <div key={b.id} data-testid={`brouillon-${b.id}`} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                  <p className="font-data text-xs text-slate-700">
                    <span className="font-bold text-slate-900">{b.extrait?.fournisseur?.nom || 'Fournisseur illisible'}</span> ·{' '}
                    {b.extrait?.numeroFacture || 'n° ?'} · {b.extrait?.date || 'date ?'} ·{' '}
                    <span className="font-bold">{fmt(b.extrait?.montantTTC ?? 0, 3)} TND TTC</span>
                    <span className="ml-2 text-slate-400">{b.fichier}</span>
                    {b.source === 'mobile' && (
                      <span data-testid={`badge-mobile-${b.id}`} className="ml-2 inline-flex items-center gap-1 border border-sky-300 bg-sky-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-sky-800">
                        <Camera size={10} /> scanné mobile · {b.le}{b.par ? ` · ${b.par}` : ''}
                      </span>
                    )}
                  </p>
                  <div className="flex gap-2">
                    <button type="button" data-testid={`ouvrir-brouillon-${b.id}`} onClick={() => setOuvert(b)} className="h-9 bg-slate-900 px-3 text-xs font-bold text-white hover:bg-slate-700">
                      Vérifier & valider
                    </button>
                    <button type="button" data-testid={`supprimer-brouillon-${b.id}`} onClick={() => { supprimerBrouillon(b.id); rafraichir(); }} className="h-9 border border-slate-300 px-2 text-slate-600 hover:bg-red-600 hover:text-white">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {qr && (
        <div data-testid="modale-qr-scan" className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-4" onClick={() => setQr(null)}>
          <div className="w-full max-w-sm border border-slate-200 bg-white p-5 text-center" onClick={(ev) => ev.stopPropagation()}>
            <p className="font-heading text-base font-bold text-slate-900">Scanner depuis le téléphone</p>
            <p className="mt-1 text-xs text-slate-500">Scannez ce QR code avec l'appareil photo du téléphone, connectez-vous, puis photographiez la facture.</p>
            <img data-testid="qr-scan-image" src={qr.image} alt="QR scan mobile" className="mx-auto mt-3 h-52 w-52" />
            <p className="mt-2 break-all font-data text-[11px] text-slate-600">{qr.lien}</p>
            <div className="mt-3 flex gap-2">
              <button type="button" data-testid="qr-copier" onClick={() => { navigator.clipboard?.writeText(qr.lien); toast.success('Lien copié'); }} className="h-10 flex-1 border border-slate-300 text-xs font-bold text-slate-700 hover:bg-slate-100">
                Copier le lien
              </button>
              <button type="button" data-testid="qr-fermer" onClick={() => setQr(null)} className="h-10 flex-1 bg-slate-900 text-xs font-bold text-white hover:bg-slate-700">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {ouvert && (
        <ValidationBrouillon
          brouillon={ouvert}
          avanceDispo={(tiersId) => soldeAvance(tiersId, avances)}
          onClose={() => setOuvert(null)}
          onValide={() => {
            supprimerBrouillon(ouvert.id);
            rafraichir();
            setOuvert(null);
            onDone();
          }}
        />
      )}
    </>
  );
};

const ValidationBrouillon = ({ brouillon, onClose, onValide }) => {
  const e = brouillon.extrait ?? {};
  const [f, setF] = useState({
    date: e.date || new Date().toISOString().slice(0, 10),
    numeroFacture: e.numeroFacture || '',
    categorie: '',
    libelle: e.objet || '',
    ventilation: (e.ventilation ?? []).length ? e.ventilation.map((l) => ({ libelle: l.libelle || '', ht: l.ht ?? 0, tauxTVA: l.tauxTVA ?? 19 })) : [{ libelle: '', ht: e.totalHT ?? 0, tauxTVA: 19 }],
    autresTaxes: e.fodec ?? 0,
    timbreFiscal: e.timbreFiscal ?? TIMBRE_FISCAL,
    appliquerRS: false,
    tauxRS: 3,
  });
  const [enCours, setEnCours] = useState(false);

  const calcul = useMemo(() => {
    const ht = f.ventilation.reduce((s, l) => s + (Number(l.ht) || 0), 0);
    const taxes = Number(f.autresTaxes) || 0;
    const timbre = Number(f.timbreFiscal) || 0;
    const tauxMax = Math.max(...f.ventilation.map((l) => Number(l.tauxTVA) || 0));
    // Deux présentations de facture possibles pour le FODEC : au prorata du HT, ou
    // intégralement sur la ligne au taux le plus élevé. On retient celle qui colle au TTC du document.
    const tvaSelon = (mode) =>
      f.ventilation.reduce((s, l) => {
        const htL = Number(l.ht) || 0;
        const taux = Number(l.tauxTVA) || 0;
        const part = mode === 'tauxmax' ? (taux === tauxMax ? taxes : 0) : ht > 0 ? (taxes * htL) / ht : 0;
        return s + ((htL + part) * taux) / 100;
      }, 0);
    const cible = Number(e.montantTTC) || 0;
    const variantes = ['prorata', 'tauxmax'].map((mode) => {
      const tva = Number(tvaSelon(mode).toFixed(3));
      const ttc = Number((ht + taxes + tva + timbre).toFixed(3));
      return { mode, tva, ttc, ecart: cible > 0 ? Math.abs(ttc - cible) : 0 };
    });
    const retenu = variantes.reduce((a, b) => (b.ecart < a.ecart ? b : a));
    const rs = f.appliquerRS ? Number(((retenu.ttc * Number(f.tauxRS)) / 100).toFixed(3)) : 0;
    return { ht, taxes, tva: retenu.tva, ttc: retenu.ttc, mode: retenu.mode, rs };
  }, [f, e.montantTTC]);
  const ecart = Number((calcul.ttc - (Number(e.montantTTC) || 0)).toFixed(3));

  const valider = async () => {
    setEnCours(true);
    const res = await resoudreFournisseurGeneral(e.fournisseur ?? {});
    if (res.error) { setEnCours(false); return toast.error(res.error); }
    const { error, depense } = await creerDepense({ ...f, tiersId: res.tiersId, fodecMode: calcul.mode, tauxRS: f.appliquerRS ? Number(f.tauxRS) : 0 });
    setEnCours(false);
    if (error) return toast.error(error);
    toast.success(
      `${depense.numeroFacture} validée${res.cree ? ` — fournisseur « ${res.nom} » créé` : ''}${
        depense.avanceImputee > 0 ? ` — avance de ${fmt(depense.avanceImputee, 3)} TND imputée` : ''
      } — reste à payer ${fmt(depense.netAPayer, 3)} TND`
    );
    onValide();
  };

  return (
    <div data-testid="validation-brouillon" className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 p-4">
      <div className="grid max-h-[92vh] w-full max-w-6xl grid-cols-1 overflow-y-auto border border-slate-200 bg-white lg:grid-cols-2">
        <div className="border-b border-slate-200 bg-slate-100 p-3 lg:border-b-0 lg:border-r">
          <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Document scanné — {brouillon.fichier}</p>
          {brouillon.apercu ? (
            brouillon.mime?.startsWith('image') ? (
              <img data-testid="apercu-document" src={brouillon.apercu} alt="facture" className="max-h-[70vh] w-full object-contain" />
            ) : (
              <iframe data-testid="apercu-document" title="facture" src={brouillon.apercu} className="h-[70vh] w-full border border-slate-300 bg-white" />
            )
          ) : (
            <p className="border border-dashed border-slate-300 bg-white px-3 py-10 text-center text-xs text-slate-500">
              Aperçu indisponible (document trop volumineux) — les données extraites restent vérifiables.
            </p>
          )}
        </div>

        <div className="p-4">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="font-heading text-lg font-black text-slate-900">Vérification des données extraites</h2>
              <p data-testid="brouillon-fournisseur" className="font-data text-xs text-slate-600">
                Fournisseur détecté : <span className="font-bold">{e.fournisseur?.nom || '—'}</span>
                {e.fournisseur?.mf ? ` · MF ${e.fournisseur.mf}` : ''}
              </p>
            </div>
            <button type="button" data-testid="fermer-brouillon" onClick={onClose} className="border border-slate-200 p-2 text-slate-500 hover:bg-slate-900 hover:text-white">
              <X size={16} />
            </button>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Date</label>
              <input data-testid="br-date" type="date" value={f.date} onChange={(ev) => setF({ ...f, date: ev.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={label}>N° de facture</label>
              <input data-testid="br-numero" value={f.numeroFacture} onChange={(ev) => setF({ ...f, numeroFacture: ev.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={label}>Catégorie analytique *</label>
              <SelectCategorieDepense usage="frais-generaux" data-testid="br-categorie" value={f.categorie} onChange={(ev) => setF({ ...f, categorie: ev.target.value })} className={inputCls}>
                <option value="">— Choisir —</option>
              </SelectCategorieDepense>
            </div>
            <div>
              <label className={label}>Objet / libellé</label>
              <input data-testid="br-libelle" value={f.libelle} onChange={(ev) => setF({ ...f, libelle: ev.target.value })} className={inputCls} />
            </div>
          </div>

          <p className="mt-4 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Ventilation multi-TVA</p>
          <div data-testid="br-ventilation" className="mt-1.5 space-y-2">
            {f.ventilation.map((l, i) => (
              <div key={i} className="flex items-center gap-2">
                <input data-testid={`br-ligne-libelle-${i}`} value={l.libelle} placeholder="libellé" onChange={(ev) => setF((p) => ({ ...p, ventilation: p.ventilation.map((x, j) => (j === i ? { ...x, libelle: ev.target.value } : x)) }))} className={`${inputCls} flex-1`} />
                <input data-testid={`br-ligne-ht-${i}`} type="number" step="0.001" value={l.ht} onChange={(ev) => setF((p) => ({ ...p, ventilation: p.ventilation.map((x, j) => (j === i ? { ...x, ht: ev.target.value } : x)) }))} className={`${inputCls} w-28 text-right`} />
                <select data-testid={`br-ligne-tva-${i}`} value={l.tauxTVA} onChange={(ev) => setF((p) => ({ ...p, ventilation: p.ventilation.map((x, j) => (j === i ? { ...x, tauxTVA: Number(ev.target.value) } : x)) }))} className={`${inputCls} w-24`}>
                  {TAUX_TVA.map((t) => <option key={t} value={t}>{`${t} %`}</option>)}
                </select>
                <button type="button" data-testid={`br-retirer-${i}`} onClick={() => setF((p) => ({ ...p, ventilation: p.ventilation.length === 1 ? p.ventilation : p.ventilation.filter((_, j) => j !== i) }))} className="border border-slate-300 p-2 text-slate-500 hover:bg-red-600 hover:text-white">
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
            <button type="button" data-testid="br-ajouter-ligne" onClick={() => setF((p) => ({ ...p, ventilation: [...p.ventilation, { libelle: '', ht: '', tauxTVA: 7 }] }))} className="border border-slate-900 px-2 py-1 text-xs font-bold text-slate-900 hover:bg-slate-900 hover:text-white">
              + Ajouter un taux
            </button>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <label className={label}>FODEC / autres taxes</label>
              <input data-testid="br-fodec" type="number" step="0.001" value={f.autresTaxes} onChange={(ev) => setF({ ...f, autresTaxes: ev.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={label}>Timbre fiscal</label>
              <input data-testid="br-timbre" type="number" step="0.001" value={f.timbreFiscal} onChange={(ev) => setF({ ...f, timbreFiscal: ev.target.value })} className={inputCls} />
            </div>
          </div>

          <label className="mt-3 flex items-center gap-2 border-l-4 border-red-600 bg-red-50/60 px-2 py-1.5 text-sm font-bold text-slate-900">
            <input data-testid="br-rs" type="checkbox" checked={f.appliquerRS} onChange={(ev) => setF({ ...f, appliquerRS: ev.target.checked })} className="h-4 w-4 accent-red-700" />
            Retenue à la source
            {f.appliquerRS && (
              <select data-testid="br-taux-rs" value={f.tauxRS} onChange={(ev) => setF({ ...f, tauxRS: Number(ev.target.value) })} className={`${inputCls} w-24`}>
                {TAUX_RS.map((t) => <option key={t} value={t}>{`${String(t).replace('.', ',')} %`}</option>)}
              </select>
            )}
            {f.appliquerRS && <span className="font-data text-xs text-red-800">− {fmt(calcul.rs, 3)} TND</span>}
          </label>

          <div data-testid="br-totaux" className="mt-3 border border-slate-300 bg-slate-50 px-3 py-2 font-data text-xs text-slate-700">
            HT {fmt(calcul.ht, 3)} · FODEC {fmt(calcul.taxes, 3)} · TVA {fmt(calcul.tva, 3)} · timbre{' '}
            {fmt(Number(f.timbreFiscal) || 0, 3)} →{' '}
            <span className="text-base font-bold text-slate-900">TTC {fmt(calcul.ttc, 3)} TND</span>
            {Number(e.montantTTC) > 0 && (
              <span data-testid="br-ecart" className={`ml-2 border px-1.5 py-0.5 text-[10px] font-bold uppercase ${Math.abs(ecart) <= 0.05 ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : Math.abs(ecart) <= 1.5 ? 'border-amber-400 bg-amber-50 text-amber-800' : 'border-red-400 bg-red-50 text-red-700'}`}>
                {Math.abs(ecart) <= 0.05
                  ? 'Concorde avec le document'
                  : `Écart ${fmt(ecart, 3)} vs document (${fmt(e.montantTTC, 3)}) — vérifiez les lignes`}
              </span>
            )}
          </div>

          <div className="mt-4 flex gap-3">
            <button
              type="button"
              data-testid="br-valider"
              disabled={enCours || !f.categorie || calcul.ht <= 0}
              onClick={valider}
              className={`flex h-11 flex-1 items-center justify-center gap-2 font-heading text-sm font-bold uppercase tracking-[0.06em] text-white ${enCours || !f.categorie || calcul.ht <= 0 ? 'cursor-not-allowed bg-slate-300' : 'bg-emerald-700 hover:bg-emerald-800'}`}
            >
              <Check size={16} /> {enCours ? 'Enregistrement…' : 'Valider la facture'}
            </button>
            <button type="button" data-testid="br-annuler" onClick={onClose} className="h-11 border border-slate-300 px-5 text-sm font-semibold text-slate-700 hover:bg-slate-100">
              Plus tard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
